import React, { useState, useMemo, useCallback, useRef } from 'react';
import type { TelemetrySample, CornerData } from '@/types/telemetry';
import type { TrackShape, TrackAnnotation } from '@/lib/spline';
import { sampleClosedSpline, computeRibbonBoundaries } from '@/lib/spline';
import { InteractiveTrackMap } from '@/components/common/trackmap/InteractiveTrackMap';
import type { OverlayUtils } from '@/components/common/trackmap/InteractiveTrackMap';
import { haversineDistanceMeters } from '@/lib/map/mapGeometry';
import type { Point2D, GeoCoord } from '@/lib/map/mapGeometry';
import {
  drawCornerTrackHighlights,
  getCornerSplineIndices,
  getCornerGpsIndices,
} from '@/lib/map/trackGeometry';
import type { CornerHighlightItem, CornerCanvasHitItem } from '@/lib/map/trackGeometry';

interface CornerMapProps {
  samples: TelemetrySample[];
  corners: CornerData[];
  selectedCornerIndex: number;
  onSelectCorner: (index: number) => void;
  trackShape?: TrackShape | null;
  trackAnnotations?: TrackAnnotation[];
}

export function CornerMap({
  samples,
  corners,
  selectedCornerIndex,
  onSelectCorner,
  trackShape,
  trackAnnotations,
}: CornerMapProps) {
  const [hoveredCornerIndex, setHoveredCornerIndex] = useState<number>(-1);

  const referencePoints = useMemo(() => {
    return samples
      .filter(s => s.latitudeRaw && s.longitudeRaw)
      .map(s => ({ lat: s.latitudeRaw / 1e6, lng: s.longitudeRaw / 1e6 }));
  }, [samples]);

  const hasShape = !!(trackShape && trackShape.centerline && trackShape.centerline.length >= 3);
  const trackWidthM = trackShape?.trackWidthM || 8.0;

  const splineSamples = useMemo(() => {
    if (!hasShape) return [];
    return sampleClosedSpline(trackShape!.centerline, trackWidthM, 30);
  }, [hasShape, trackShape, trackWidthM]);

  const boundaries = useMemo(() => {
    if (!hasShape) return { inner: [], outer: [], totalLengthM: 0 };
    return computeRibbonBoundaries(trackShape!.centerline, trackWidthM, 30);
  }, [hasShape, trackShape, trackWidthM]);

  const cornerItems = useMemo<CornerHighlightItem[]>(() => {
    return corners.map((c, i) => ({
      index: i,
      orderIndex: i + 1,
      label: String(i + 1),
      apexPoint: c.apexPoint,
      entryPoint: c.entryPoint,
      exitPoint: c.exitPoint,
    }));
  }, [corners]);

  // Pre-calculate geo points along each corner span for fast hit-testing
  const cornerSpans = useMemo(() => {
    return corners.map((c, i) => {
      if (!c.apexPoint) return { index: i, points: [] };
      if (hasShape && splineSamples.length > 0) {
        const indices = getCornerSplineIndices(c.entryPoint, c.apexPoint, c.exitPoint, splineSamples);
        return {
          index: i,
          points: indices.map(idx => splineSamples[idx].point),
        };
      }
      if (referencePoints.length > 1) {
        const indices = getCornerGpsIndices(c.entryPoint, c.apexPoint, c.exitPoint, referencePoints);
        return {
          index: i,
          points: indices.map(idx => referencePoints[idx]),
        };
      }
      return { index: i, points: [c.apexPoint] };
    });
  }, [corners, hasShape, splineSamples, referencePoints]);

  const cornerHitItemsRef = useRef<CornerCanvasHitItem[]>([]);

  const renderOverlay = useCallback(
    (ctx: CanvasRenderingContext2D, { toCanvas, anchor }: OverlayUtils) => {
      cornerHitItemsRef.current = drawCornerTrackHighlights(ctx, cornerItems, anchor, toCanvas, {
        selectedIndex: selectedCornerIndex,
        hoveredIndex: hoveredCornerIndex,
        showBadges: true,
        splineSamples,
        boundaries,
        gpsPoints: referencePoints,
      });
    },
    [cornerItems, selectedCornerIndex, hoveredCornerIndex, splineSamples, boundaries, referencePoints]
  );

  const findHitCorner = useCallback(
    (canvasPt: Point2D, geoPt: GeoCoord | null) => {
      if (!corners || corners.length === 0) return -1;

      // 1. Direct canvas badge hit test (within 22px radius)
      for (const item of cornerHitItemsRef.current) {
        const distCanvas = Math.hypot(canvasPt.x - item.apex.x, canvasPt.y - item.apex.y);
        if (distCanvas <= 22) {
          return item.index;
        }
      }

      // 2. Geographic distance check: anywhere along corner track span (within 25m) or apex (within 35m)
      if (geoPt) {
        let bestIdx = -1;
        let bestDistM = Infinity;

        for (const span of cornerSpans) {
          for (const pt of span.points) {
            const dist = haversineDistanceMeters(pt, geoPt);
            if (dist <= 25 && dist < bestDistM) {
              bestDistM = dist;
              bestIdx = span.index;
            }
          }
        }

        if (bestIdx >= 0) {
          return bestIdx;
        }
      }

      return -1;
    },
    [corners, cornerSpans]
  );

  const handleClick = useCallback(
    (canvasPt: Point2D, _metricPt: Point2D, geoPt: GeoCoord) => {
      const hitIdx = findHitCorner(canvasPt, geoPt);
      if (hitIdx >= 0) {
        onSelectCorner(hitIdx);
      }
    },
    [findHitCorner, onSelectCorner]
  );

  const handleHover = useCallback(
    (canvasPt: Point2D | null, _metricPt: Point2D | null, geoPt: GeoCoord | null) => {
      if (!canvasPt) {
        setHoveredCornerIndex(-1);
        return;
      }
      const hitIdx = findHitCorner(canvasPt, geoPt);
      setHoveredCornerIndex(hitIdx);
    },
    [findHitCorner]
  );

  return (
    <div className="relative aspect-square w-full mx-auto overflow-hidden rounded-2xl shadow-2xl border border-white/10">
      <InteractiveTrackMap
        trackShape={trackShape}
        trackAnnotations={trackAnnotations}
        points={referencePoints}
        renderOverlay={renderOverlay}
        onClick={handleClick}
        onHover={handleHover}
        cursor={hoveredCornerIndex >= 0 ? 'pointer' : undefined}
        className="w-full h-full"
      />
    </div>
  );
}
